<script>
	import {
		extractErrorMessage,
		isVideoFile,
		scrollToTop,
	} from "src/utils/misc";
	import { createEventDispatcher } from "svelte";
	import VideoThumbnail from "../VideoPlayer/VideoThumbnail.svelte";
	import {
		imageLightbox,
		imageLightboxIndex,
		isLightBoxOpen,
	} from "src/store/store";
	export let title = "";
	export let fileType = "image"; // image || file
	export let name = ""; // e.g "document.pdf"
	export let files = [];
	export let handleFileChange = () => {};
	export let multiple = true; // allow multiple files
	export let maxFileSize = 10 * 1024 * 1024; // 10 MB
	export let isEdit = true;
	export let isRequired = false;

	const dispatch = createEventDispatcher();

	const fileTypes = {
		image: ".png, .jpeg, .jpg",
		file: ".xls, .xlsx, .pdf",
		video: ".mp4, .mov",
		media: ".png, .jpeg, .jpg, .mp4, .mov, .avi, .wmv",
	};

	const fileDescriptions = {
		image: `PNG, JPG or JPEG only. Must be lesser than ${
			maxFileSize / 1024 / 1024
		}MB.`,
		file: `PDF or XLS files only. Total upload size per submission must not exceed ${
			maxFileSize / 1024 / 1024
		}MB.`,
		media: `PNG, JPG, JPEG, MP4, or MOV files only. Total upload size per submission must not exceed 1GB`,
	};

	// check if given item is a File object (not string/URL)
	function isFileObject(item) {
		return item instanceof File;
	}

	// pass 'remove' event to parent with the index of the file to remove
	function removeAt(index) {
		dispatch("remove", { index });
	}

	// called when user selects files via input
	function onInputChange(e) {
		Array.from(e.target.files).forEach((val) => {
			if (val.size > maxFileSize) {
				let maxFileSizeinGB = maxFileSize / 1024 / 1024 / 1024;
				let maxFileSizeinMB = maxFileSize / 1024 / 1024;

				extractErrorMessage(
					`File ${val.name} exceeds the maximum size of ${
						val.size < 1024 * 1024 * 1024
							? maxFileSizeinMB
							: maxFileSizeinGB
					} ${val.size < 1024 * 1024 * 1024 ? "MB" : "GB"}.`
				);
				scrollToTop();
			}
		});

		let selected = Array.from(e.target.files).filter(
			(f) => f.size <= maxFileSize
		);

		if (selected.length > 0) {
			handleFileChange({
				target: { files: selected, name: e.target.name },
			});
		}
		// clear input so same file can be picked again
		e.target.value = null;
	}
</script>

<div class="my-3">
	<div class="my-2">
		{title}
		{#if isRequired}
			<span class="text-danger">*</span>
		{/if}
	</div>
	<div class="d-flex">
		{#if (fileType === "image" || fileType === "media") && files.length > 0}
			{#each files as { file, canDelete }, i}
				<div
					class="thumbnail-wrapper"
					on:click={() => {
						imageLightbox.set(files.map((f) => f.file));
						imageLightboxIndex.set(i);
						isLightBoxOpen.set(true);
					}}
					on:keydown={() => {}}
				>
					{#if canDelete}
						<button
							class="delete-icon"
							type="button"
							on:click={(e) => {
								e.stopPropagation();
								removeAt(i);
							}}
							on:keydown={(e) => {
								if (e.key === "Enter" || e.key === " ") {
									e.stopPropagation();
									removeAt(i);
								}
							}}
						/>
					{/if}
					{#if isFileObject(file)}
						{#if file.type?.startsWith("video/")}
							<video
								src={URL.createObjectURL(file)}
								alt={file?.name}
								class="thumbnail"
								on:keydown={() => {}}
							>
								<track kind="captions" label="Captions" />
							</video>
						{:else}
							<img
								src={URL.createObjectURL(file)}
								alt="thumbnail"
								class="thumbnail"
							/>
						{/if}
					{:else if isVideoFile(file)}
						<VideoThumbnail url={file} />
					{:else}
						<img
							src={file}
							alt={file?.split("/").pop()}
							class="thumbnail"
							on:click={() => isLightBoxOpen.set(true)}
						/>
					{/if}
				</div>
			{/each}
		{:else if fileType === "file"}
			<div>
				{#each files as { file, canDelete }, i}
					<div class="file-item">
						{#if isFileObject(file)}
							<a
								class="link link-text"
								href={URL.createObjectURL(file)}
								target="_blank">{file.name}</a
							>
						{:else}
							<a
								class="link link-text"
								href={file}
								target="_blank">{file.split("/").pop()}</a
							>
						{/if}
						{#if canDelete}
							<button
								class="remove-icon"
								type="button"
								on:click={() => removeAt(i)}
								on:keydown={(e) =>
									(e.key === "Enter" || e.key === " ") &&
									removeAt(i)}
							/>
						{/if}
					</div>
				{/each}
			</div>
		{:else}
			<span />
		{/if}
	</div>

	{#if isEdit}
		<div>
			<div
				class="card-box"
				on:click={() =>
					document.getElementById(`file-input-${fileType}`).click()}
				on:keydown={() => {}}
			>
				<div class="header">
					<div class="upload-area">
						<div
							class="d-flex justify-content-center align-items-center"
						>
							<img src="/assets/icon-share.svg" alt="upload" />
						</div>
						<div class="upload-text">Upload or drop file here</div>
						<input
							id="file-input-{fileType}"
							multiple
							type="file"
							accept={fileTypes[fileType]}
							style="display: none;"
							aria-label="Upload file"
							on:change={(e) => onInputChange(e)}
							{name}
						/>
						<div class="upload-desc">
							{fileDescriptions[fileType]}
						</div>
					</div>
				</div>
			</div>
		</div>
	{/if}
</div>

<style>
	.card-box {
		background: white;
		border: 1px dashed var(--fds-outline);
		padding: 16px 24px;
		width: 100%;
		box-sizing: border-box;
		display: flex;
		justify-content: center;
	}

	.header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		cursor: pointer;
	}

	.upload-area {
		width: 100%;
	}

	.upload-text {
		color: var(--fds-primary);
		text-decoration: underline;
		font-size: 16px;
		text-align: center;
	}

	.upload-desc {
		text-align: center;
		color: var(--fds-on-surface-variant);
		font-size: 14px;
	}
	.thumbnail {
		width: 40px;
		height: 40px;
		object-fit: cover;
		margin-right: 10px;
		margin-top: 5px;
		margin-bottom: 5px;
	}

	.thumbnail-wrapper {
		position: relative;
		display: inline-block;
		margin: 0 5px;
		cursor: pointer;
		height: 50px;
	}

	.delete-icon {
		position: absolute;
		top: -11px;
		right: -7px;
		width: 24px;
		height: 24px;
		background-image: url("/assets/icon-cancel-circle.svg");
		background-size: cover;
		background-repeat: no-repeat;
		z-index: 2;
		background-color: transparent;
		border: none;
		cursor: pointer;
	}

	.file-item {
		display: flex;
		align-items: center;
	}

	.file-item a {
		text-decoration: underline;
		color: var(--fds-primary);
	}

	.remove-icon {
		width: 20px;
		height: 20px;
		background-image: url("/assets/icon-cancel-circle.svg");
		background-size: cover;
		background-repeat: no-repeat;
		background-color: transparent;
		border: none;
		cursor: pointer;
		margin-left: 10px;
		flex-shrink: 0;
	}
</style>
